import React from "react";
import {Box, Typography} from "@material-ui/core/";
import MenuBookOutlinedIcon from "@material-ui/icons/MenuBookOutlined";
import clsx from "clsx";
import useTermDescriptionWithVariables from "hooks/show/glossary/useTermDescriptionWithVariables";
import {ICategory, IGlossary, ITerm} from "@evennt-tech/api-v2-core";
import GlossaryTermDescription from "view/components/GlossaryTerm/Description";
import {isLinkedTerm} from "api/showInfoLibrary/types";
import GlossaryTermActions, {Actions} from "./GlossaryTermActions";
import useStyles from "./styles";

export type GlossaryTermProps = {
  term: ITerm;
  glossary: IGlossary;
  actions?: Actions;
  terms: ITerm[];
  categories: ICategory[];
  isPrintView?: boolean;
};

const GlossaryTerm: React.FC<GlossaryTermProps> = (args) => {
  const {term, actions, terms, categories, isPrintView} = args;
  const classes = useStyles({interactive: !!actions});
  const description = useTermDescriptionWithVariables(term.description);

  const isLinked = isLinkedTerm(term);
  /*
   * The badge and the tint are organiser-only: where content came from is no
   * concern of an exhibitor's. `actions` is the marker — `readonly` callers pass
   * none — and print keeps the card but drops the badge.
   */
  const showLinkedState = isLinked && !!actions;
  const showLinkedBadge = showLinkedState && !isPrintView;

  return (
    <div className={clsx(classes.root, {[classes.linked]: showLinkedState})}>
      {showLinkedBadge && (
        <Box className={classes.linkedBadge}>
          linked to Library
          <MenuBookOutlinedIcon className={classes.linkedBadgeIcon} />
        </Box>
      )}
      <Box className={classes.header}>
        <Box display="flex" position="relative">
          <Typography variant="h2" className={classes.title}>
            {term.title}
          </Typography>
        </Box>
        <GlossaryTermActions
          term={term}
          terms={terms}
          categories={categories}
          actions={actions}
          isPrintView={isPrintView}
        />
      </Box>
      <GlossaryTermDescription description={description} />
    </div>
  );
};

export default React.memo(GlossaryTerm, (prev, next) => {
  // Default shallow compare fails when the parent rebuilds the term object
  // (e.g. via filter/map) on every render. Compare by content so the
  // dangerouslySetInnerHTML downstream doesn't get reapplied.
  return (
    prev.term.id === next.term.id &&
    prev.term.title === next.term.title &&
    prev.term.description === next.term.description &&
    isLinkedTerm(prev.term) === isLinkedTerm(next.term) &&
    prev.actions === next.actions &&
    prev.isPrintView === next.isPrintView
  );
});
